import jsPDF from "jspdf";
import { Employee, EmployeeEvaluation } from "@/types/employee";
import { Criteria } from "@/types/criteria";
import { CompanyData, getCompanyData } from "@/components/CompanySettings";

interface PdfOptions {
  createdByName?: string;
}

// Corporate Design Colors - derived from primary brand color
// These colors create a cohesive look across all PDFs
const CORPORATE_COLORS = {
  // Primary brand color (deep blue) - used for headers, accents
  primary: [25, 65, 130] as [number, number, number],
  primaryLight: [45, 90, 165] as [number, number, number],
  primaryDark: [15, 45, 95] as [number, number, number],
  
  // Secondary colors
  secondary: [100, 116, 139] as [number, number, number],
  
  // Semantic colors
  success: [34, 120, 80] as [number, number, number],
  warning: [180, 120, 20] as [number, number, number],
  danger: [180, 50, 50] as [number, number, number],
  
  // Text colors
  text: [30, 41, 59] as [number, number, number],
  textLight: [100, 116, 139] as [number, number, number],
  
  // Background colors
  background: [248, 250, 252] as [number, number, number],
  backgroundAccent: [235, 242, 250] as [number, number, number],
  
  // Border/Line colors
  border: [226, 232, 240] as [number, number, number],
  
  // Table colors
  tableHeader: [25, 65, 130] as [number, number, number],
  tableRowAlt: [248, 250, 252] as [number, number, number],
};

/**
 * Loads image from URL and returns base64 data
 */
const loadImageAsBase64 = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn("Could not load image:", e);
    return null;
  }
};

const detectIsInIframe = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

const detectIsIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";

  // iPadOS 13+ reports as Mac but has touch points
  const iPadOS13Plus =
    navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1;

  return /iPad|iPhone|iPod/.test(ua) || iPadOS13Plus;
};

/**
 * Adds company logo to top left corner of the page - Corporate Header Design
 */
const addCompanyHeader = async (doc: jsPDF, companyData: CompanyData, logoBase64?: string | null) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Logo oben links - größer und prominenter
  if (logoBase64) {
    try {
      doc.addImage(logoBase64, 'AUTO', 15, 8, 45, 25);
    } catch (e) {
      console.warn("Logo konnte nicht geladen werden:", e);
    }
  }
  
  // Optional: Add thin accent line under header area
  doc.setDrawColor(...CORPORATE_COLORS.primary);
  doc.setLineWidth(0.8);
  doc.line(15, 36, pageWidth - 15, 36);
};

/**
 * Adds enhanced corporate footer with:
 * - Left: Company address with line breaks
 * - Center: "Erstellt mit HR Flash" + date
 * - Right: Page number
 */
const addCompanyFooter = (doc: jsPDF, companyData: CompanyData, createdByName?: string) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageCount = doc.getNumberOfPages();
  
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    // Footer background with subtle gradient effect
    doc.setFillColor(...CORPORATE_COLORS.background);
    doc.rect(0, pageHeight - 35, pageWidth, 35, 'F');
    
    // Trennlinie oben in Corporate Farbe
    doc.setDrawColor(...CORPORATE_COLORS.primary);
    doc.setLineWidth(0.8);
    doc.line(15, pageHeight - 35, pageWidth - 15, pageHeight - 35);
    
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    
    // === LINKS: Firmenadresse mit Zeilenumbrüchen ===
    let footerY = pageHeight - 28;
    doc.setTextColor(...CORPORATE_COLORS.text);
    
    if (companyData.name) {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...CORPORATE_COLORS.primary);
      doc.text(companyData.name, 15, footerY);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...CORPORATE_COLORS.text);
      footerY += 4;
    }
    
    const streetFull = [companyData.street, companyData.houseNumber].filter(Boolean).join(" ");
    if (streetFull) {
      doc.text(streetFull, 15, footerY);
      footerY += 4;
    }
    
    const cityFull = [companyData.postalCode, companyData.city].filter(Boolean).join(" ");
    if (cityFull) {
      doc.text(cityFull, 15, footerY);
      footerY += 4;
    }
    
    // Kontaktdaten
    doc.setTextColor(...CORPORATE_COLORS.textLight);
    const contactParts: string[] = [];
    if (companyData.phone) contactParts.push(`Tel: ${companyData.phone}`);
    if (companyData.email) contactParts.push(companyData.email);
    if (contactParts.length > 0) {
      doc.text(contactParts.join(" | "), 15, footerY);
    }
    
    // === MITTE: "Erstellt mit HR Flash" + Datum ===
    const now = new Date();
    const dateStr = now.toLocaleDateString("de-DE", { 
      day: "2-digit", 
      month: "2-digit", 
      year: "numeric" 
    });
    
    doc.setTextColor(...CORPORATE_COLORS.primary);
    doc.setFont("helvetica", "bold");
    doc.text("Erstellt mit HR Flash", pageWidth / 2, pageHeight - 24, { align: "center" });
    
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.textLight);
    doc.text(dateStr, pageWidth / 2, pageHeight - 20, { align: "center" });
    
    if (createdByName) {
      doc.setFontSize(6);
      doc.text(`von ${createdByName}`, pageWidth / 2, pageHeight - 16, { align: "center" });
    }
    
    // === RECHTS: Seitenzahl ===
    doc.setFontSize(9);
    doc.setTextColor(...CORPORATE_COLORS.primary);
    doc.setFont("helvetica", "bold");
    doc.text(
      `Seite ${i} / ${pageCount}`,
      pageWidth - 15,
      pageHeight - 22,
      { align: "right" }
    );
    
    doc.setTextColor(0, 0, 0);
  }
};

/**
 * Draws a styled section header in corporate design
 */
const drawSectionHeader = (doc: jsPDF, title: string, yPosition: number, pageWidth: number): number => {
  doc.setFillColor(...CORPORATE_COLORS.primary);
  doc.rect(15, yPosition - 5, pageWidth - 30, 10, 'F');
  
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text(title, 20, yPosition + 2);
  doc.setTextColor(0, 0, 0);
  
  return yPosition + 12;
};

/**
 * Draws a styled info box in corporate design
 */
const drawInfoBox = (doc: jsPDF, x: number, y: number, width: number, height: number): void => {
  doc.setFillColor(...CORPORATE_COLORS.backgroundAccent);
  doc.setDrawColor(...CORPORATE_COLORS.border);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, width, height, 3, 3, 'FD');
};

/**
 * Draws document title in corporate design
 */
const drawDocumentTitle = (doc: jsPDF, title: string, subtitle?: string, yPosition: number = 45): number => {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Main title
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...CORPORATE_COLORS.primary);
  doc.text(title, pageWidth / 2, yPosition, { align: "center" });
  
  let currentY = yPosition + 10;
  
  // Subtitle if provided
  if (subtitle) {
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.text);
    doc.text(subtitle, pageWidth / 2, currentY, { align: "center" });
    currentY += 12;
  }
  
  doc.setTextColor(0, 0, 0);
  return currentY;
};

/**
 * Draws a table header row in corporate design
 */
const drawTableHeader = (doc: jsPDF, headers: { text: string; x: number }[], yPosition: number, pageWidth: number): number => {
  doc.setFillColor(...CORPORATE_COLORS.tableHeader);
  doc.rect(15, yPosition - 5, pageWidth - 30, 10, 'F');
  
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  
  headers.forEach(header => {
    doc.text(header.text, header.x, yPosition);
  });
  
  doc.setTextColor(0, 0, 0);
  return yPosition + 10;
};

/**
 * Draws a summary box in corporate design
 */
const drawSummaryBox = (doc: jsPDF, label: string, value: string, yPosition: number, pageWidth: number): number => {
  doc.setFillColor(...CORPORATE_COLORS.backgroundAccent);
  doc.setDrawColor(...CORPORATE_COLORS.primary);
  doc.setLineWidth(0.5);
  doc.rect(15, yPosition - 5, pageWidth - 30, 14, 'FD');
  
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...CORPORATE_COLORS.primary);
  doc.text(label, 20, yPosition + 2);
  doc.text(value, pageWidth - 20, yPosition + 2, { align: "right" });
  
  doc.setTextColor(0, 0, 0);
  return yPosition + 18;
};

/**
 * Checks if new page is needed and adds header if so
 */
const checkNewPage = (doc: jsPDF, yPosition: number, threshold: number, logoBase64?: string | null): number => {
  if (yPosition > threshold) {
    doc.addPage();
    if (logoBase64) {
      try { doc.addImage(logoBase64, 'AUTO', 15, 8, 45, 25); } catch (e) {}
    }
    // Add header line on new page
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setDrawColor(...CORPORATE_COLORS.primary);
    doc.setLineWidth(0.8);
    doc.line(15, 36, pageWidth - 15, 36);
    return 45;
  }
  return yPosition;
};

export const exportEvaluationToPDF = async (
  employee: Employee,
  evaluation: EmployeeEvaluation,
  criteria: Criteria[],
  options?: PdfOptions
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;
  
  // Company Header
  await addCompanyHeader(doc, companyData, logoBase64);
  
  let yPosition = 45;

  // Title
  yPosition = drawDocumentTitle(doc, "Mitarbeiter Bewertung", `${employee.name} - ${employee.position}`, yPosition);
  
  // Evaluation period
  const monthDate = new Date(evaluation.month + "-01");
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...CORPORATE_COLORS.textLight);
  doc.text(
    `Bewertungszeitraum: ${monthDate.toLocaleDateString("de-DE", { month: "long", year: "numeric" })}`,
    pageWidth / 2,
    yPosition,
    { align: "center" }
  );
  yPosition += 15;

  // Separator line
  doc.setDrawColor(...CORPORATE_COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(20, yPosition, pageWidth - 20, yPosition);
  yPosition += 10;

  // Mitarbeiterinformationen
  yPosition = drawSectionHeader(doc, "Mitarbeiterinformationen", yPosition, pageWidth);
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...CORPORATE_COLORS.text);
  doc.text(`Mitarbeiternummer: ${employee.employeeNumber}`, 20, yPosition);
  yPosition += 6;
  doc.text(
    `Bewertet am: ${new Date(evaluation.date).toLocaleDateString("de-DE")}`,
    20,
    yPosition
  );
  yPosition += 15;

  // Bewertungskriterien
  yPosition = drawSectionHeader(doc, "Bewertungskriterien", yPosition, pageWidth);
  
  doc.setFontSize(9);
  Object.entries(evaluation.criteria).forEach(([key, rating]) => {
    const criterion = criteria.find((c) => c.key === key);
    if (!criterion) return;

    yPosition = checkNewPage(doc, yPosition, 245, logoBase64);

    // Kriterium Name
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...CORPORATE_COLORS.primary);
    doc.circle(22, yPosition - 1.5, 1.5, 'F');
    doc.setTextColor(...CORPORATE_COLORS.text);
    doc.text(criterion.label, 26, yPosition);
    yPosition += 5;

    doc.setFont("helvetica", "normal");
    const ratingText = rating.rating
      ? rating.rating
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
      : "Nicht bewertet";
    
    // Bewertung mit Farbe
    let ratingColor = CORPORATE_COLORS.textLight;
    if (rating.rating === 'voll-erfuellt') ratingColor = CORPORATE_COLORS.success;
    else if (rating.rating === 'erfuellt') ratingColor = [50, 150, 80];
    else if (rating.rating === 'teilweise-erfuellt') ratingColor = CORPORATE_COLORS.warning;
    else if (rating.rating === 'nicht-erfuellt') ratingColor = CORPORATE_COLORS.danger;
    
    doc.setTextColor(ratingColor[0], ratingColor[1], ratingColor[2]);
    doc.setFont("helvetica", "bold");
    doc.text(`Bewertung: ${ratingText}`, 28, yPosition);
    yPosition += 5;
    
    doc.setTextColor(...CORPORATE_COLORS.text);
    doc.setFont("helvetica", "normal");
    doc.text(`Punkte: ${rating.points}`, 28, yPosition);
    yPosition += 5;

    if (rating.comments && rating.comments.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...CORPORATE_COLORS.textLight);
      doc.text('Kommentare:', 28, yPosition);
      yPosition += 5;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...CORPORATE_COLORS.text);
      
      rating.comments.forEach((comment) => {
        const date = new Date(comment.date).toLocaleDateString('de-DE');
        const commentText = `[${date}] ${comment.userName}: ${comment.text}`;
        const splitComment = doc.splitTextToSize(commentText, pageWidth - 60);
        doc.text(splitComment, 32, yPosition);
        yPosition += splitComment.length * 5;
      });
    }

    yPosition += 6;
  });

  // Gesamtbewertung
  yPosition = checkNewPage(doc, yPosition, 210, logoBase64);
  yPosition += 8;
  
  // Separator
  doc.setDrawColor(...CORPORATE_COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(20, yPosition, pageWidth - 20, yPosition);
  yPosition += 12;

  // Performance box
  const percentage = evaluation.maxScore > 0 ? (evaluation.totalScore / evaluation.maxScore) * 100 : 0;
  let performanceColor = CORPORATE_COLORS.textLight;
  if (percentage >= 90) performanceColor = CORPORATE_COLORS.success;
  else if (percentage >= 75) performanceColor = [50, 150, 80];
  else if (percentage >= 60) performanceColor = CORPORATE_COLORS.warning;
  else performanceColor = CORPORATE_COLORS.danger;
  
  doc.setFillColor(performanceColor[0], performanceColor[1], performanceColor[2], 0.1);
  doc.roundedRect(15, yPosition - 5, pageWidth - 30, 45, 3, 3, 'F');
  
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(performanceColor[0], performanceColor[1], performanceColor[2]);
  doc.text("Gesamtbewertung", 20, yPosition);
  yPosition += 10;

  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...CORPORATE_COLORS.text);
  doc.text(`Erreichte Punkte: ${evaluation.totalScore.toFixed(1)} von ${evaluation.maxScore}`, 20, yPosition);
  yPosition += 7;
  
  // Progress bar
  doc.setFillColor(...CORPORATE_COLORS.border);
  doc.rect(20, yPosition, 80, 5, 'F');
  doc.setFillColor(performanceColor[0], performanceColor[1], performanceColor[2]);
  doc.rect(20, yPosition, (80 * percentage) / 100, 5, 'F');
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.text(`${percentage.toFixed(1)}%`, 105, yPosition + 4);
  yPosition += 10;
  
  doc.setFontSize(13);
  doc.setTextColor(performanceColor[0], performanceColor[1], performanceColor[2]);
  doc.text(`Leistungsniveau: ${evaluation.performanceLevel}`, 20, yPosition);
  
  doc.setTextColor(0, 0, 0);

  // Company Footer
  addCompanyFooter(doc, companyData, options?.createdByName);

  // Save
  const fileName = `Bewertung_${employee.name.replace(/\s+/g, "_")}_${evaluation.month}.pdf`;
  doc.save(fileName);
};

export const exportAllEvaluationsToPDF = async (
  employees: Employee[],
  evaluations: EmployeeEvaluation[],
  criteria: Criteria[],
  options?: PdfOptions
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;
  
  // Company Header
  await addCompanyHeader(doc, companyData, logoBase64);
  
  let yPosition = 45;

  // Title
  yPosition = drawDocumentTitle(doc, "Mitarbeiter Übersicht", `Erstellt am: ${new Date().toLocaleDateString("de-DE")}`, yPosition);
  yPosition += 5;

  employees.forEach((employee, index) => {
    if (index > 0) {
      doc.addPage();
      if (logoBase64) {
        try { doc.addImage(logoBase64, 'AUTO', 15, 8, 45, 25); } catch (e) {}
      }
      doc.setDrawColor(...CORPORATE_COLORS.primary);
      doc.setLineWidth(0.8);
      doc.line(15, 36, pageWidth - 15, 36);
      yPosition = 45;
    }

    const employeeEvals = evaluations
      .filter((e) => e.employeeId === employee.id)
      .sort((a, b) => new Date(b.month).getTime() - new Date(a.month).getTime());

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...CORPORATE_COLORS.primary);
    doc.text(`${employee.name} - ${employee.position}`, 20, yPosition);
    yPosition += 10;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.text);
    doc.text(`Mitarbeiternummer: ${employee.employeeNumber}`, 20, yPosition);
    yPosition += 6;
    doc.text(`Anzahl Bewertungen: ${employeeEvals.length}`, 20, yPosition);

    if (employeeEvals.length > 0) {
      const avgScore =
        employeeEvals.reduce(
          (sum, e) => sum + (e.maxScore > 0 ? (e.totalScore / e.maxScore) * 100 : 0),
          0
        ) / employeeEvals.length;
      
      yPosition += 6;
      doc.text(`Durchschnittliche Leistung: ${avgScore.toFixed(1)}%`, 20, yPosition);
    }

    yPosition += 12;
    yPosition = drawSectionHeader(doc, "Bewertungshistorie", yPosition, pageWidth);

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    employeeEvals.forEach((evaluation) => {
      yPosition = checkNewPage(doc, yPosition, 250, logoBase64);

      const monthDate = new Date(evaluation.month + "-01");
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...CORPORATE_COLORS.primary);
      doc.text(
        `${monthDate.toLocaleDateString("de-DE", { month: "long", year: "numeric" })}`,
        25,
        yPosition
      );
      yPosition += 5;

      doc.setFont("helvetica", "normal");
      doc.setTextColor(...CORPORATE_COLORS.text);
      doc.text(`Punkte: ${evaluation.totalScore.toFixed(1)} / ${evaluation.maxScore}`, 30, yPosition);
      yPosition += 5;
      doc.text(`Leistung: ${evaluation.performanceLevel}`, 30, yPosition);
      yPosition += 8;
    });
  });

  // Company Footer
  addCompanyFooter(doc, companyData, options?.createdByName);

  doc.save(`Mitarbeiter_Uebersicht_${new Date().toISOString().split("T")[0]}.pdf`);
};

// Export employee tools to PDF
export const exportToolsToPDF = async (
  employeeName: string,
  tools: { name: string; is_assigned: boolean; serial_number?: string; replacement_cost: number; assigned_date?: string; tool_type: string }[],
  signatureUrl?: string | null,
  signedAt?: string | null,
  signedByName?: string | null,
  options?: PdfOptions
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;
  
  await addCompanyHeader(doc, companyData, logoBase64);
  let yPosition = 45;

  // Title
  yPosition = drawDocumentTitle(doc, "Werkzeug & Arbeitsmaterial", `Mitarbeiter: ${employeeName}`, yPosition);
  yPosition += 5;

  const assignedTools = tools.filter(t => t.is_assigned);
  const handTools = assignedTools.filter(t => t.tool_type === "hand_tool");
  const machines = assignedTools.filter(t => t.tool_type === "machine");

  const renderSection = (items: typeof tools, title: string) => {
    if (items.length === 0) return;
    
    yPosition = drawSectionHeader(doc, title, yPosition, pageWidth);

    let totalCost = 0;
    items.forEach(tool => {
      yPosition = checkNewPage(doc, yPosition, 260, logoBase64);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...CORPORATE_COLORS.text);
      doc.text(`• ${tool.name}`, 25, yPosition);
      
      const costText = tool.replacement_cost.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
      doc.text(costText, pageWidth - 50, yPosition, { align: "right" });
      totalCost += tool.replacement_cost;

      if (tool.serial_number) {
        yPosition += 5;
        doc.setFontSize(8);
        doc.setTextColor(...CORPORATE_COLORS.textLight);
        doc.text(`S/N: ${tool.serial_number}`, 30, yPosition);
      }

      if (tool.assigned_date) {
        doc.setFontSize(8);
        doc.setTextColor(...CORPORATE_COLORS.textLight);
        doc.text(`Übergabe: ${new Date(tool.assigned_date).toLocaleDateString("de-DE")}`, 80, yPosition);
      }

      doc.setTextColor(0, 0, 0);
      yPosition += 7;
    });

    // Total
    yPosition += 3;
    doc.setDrawColor(...CORPORATE_COLORS.border);
    doc.line(20, yPosition, pageWidth - 20, yPosition);
    yPosition += 6;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...CORPORATE_COLORS.text);
    doc.text("Summe:", 25, yPosition);
    doc.text(totalCost.toLocaleString("de-DE", { style: "currency", currency: "EUR" }), pageWidth - 50, yPosition, { align: "right" });
    yPosition += 15;
  };

  renderSection(handTools, "Handwerkzeuge");
  renderSection(machines, "Maschinen & Messgeräte");

  // Grand total
  const grandTotal = assignedTools.reduce((sum, t) => sum + t.replacement_cost, 0);
  yPosition = drawSummaryBox(doc, "Gesamtwert Werkzeug:", grandTotal.toLocaleString("de-DE", { style: "currency", currency: "EUR" }), yPosition, pageWidth);

  // Signature section
  if (signatureUrl) {
    yPosition = checkNewPage(doc, yPosition + 10, 200, logoBase64);
    
    // Signature header
    yPosition = drawSectionHeader(doc, "Bestätigung Werkzeugempfang", yPosition, pageWidth);
    
    // Signature info
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.text);
    doc.text("Hiermit bestätige ich den Empfang aller oben aufgeführten Werkzeuge und Maschinen.", 20, yPosition);
    yPosition += 10;
    
    // Add signature image
    try {
      doc.addImage(signatureUrl, 'PNG', 20, yPosition, 60, 30);
      yPosition += 35;
    } catch (e) {
      console.warn("Unterschrift konnte nicht geladen werden:", e);
    }
    
    // Signature details
    doc.setFontSize(9);
    doc.setTextColor(...CORPORATE_COLORS.textLight);
    if (signedByName) {
      doc.text(`Unterzeichnet von: ${signedByName}`, 20, yPosition);
      yPosition += 5;
    }
    if (signedAt) {
      const signedDate = new Date(signedAt);
      doc.text(`Datum: ${signedDate.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit", 
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      })} Uhr`, 20, yPosition);
    }
    doc.setTextColor(0, 0, 0);
  }

  addCompanyFooter(doc, companyData, options?.createdByName);
  doc.save(`Werkzeug_${employeeName.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`);
};

// Export employee clothing to PDF (legacy function for backwards compatibility)
export const exportClothingToPDF = async (
  employeeName: string,
  clothing: Record<string, string>,
  options?: PdfOptions
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;
  
  await addCompanyHeader(doc, companyData, logoBase64);
  let yPosition = 45;

  // Title
  yPosition = drawDocumentTitle(doc, "Arbeitskleidung", `Mitarbeiter: ${employeeName}`, yPosition);
  yPosition += 10;

  const clothingLabels: Record<string, string> = {
    pants_size: "Hosengröße",
    tshirt_size: "T-Shirt Größe",
    sweatshirt_size: "Sweatshirt Größe",
    fleece_jacket_size: "Fleecejacke Größe",
    winter_jacket_size: "Winterjacke Größe",
    work_shoes_size: "Arbeitsschuhe Größe",
  };

  Object.entries(clothingLabels).forEach(([key, label], index) => {
    // Alternating row background
    if (index % 2 === 0) {
      doc.setFillColor(...CORPORATE_COLORS.tableRowAlt);
      doc.rect(15, yPosition - 5, pageWidth - 30, 12, 'F');
    }
    
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...CORPORATE_COLORS.text);
    doc.text(label + ":", 20, yPosition);
    
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.primary);
    doc.text(clothing[key] || "-", pageWidth / 2, yPosition);
    
    yPosition += 15;
  });

  addCompanyFooter(doc, companyData, options?.createdByName);
  doc.save(`Arbeitskleidung_${employeeName.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`);
};

interface CustomItemPDF {
  id: string;
  name: string;
  size: string;
}

interface ClothingAndSafetyData {
  pants_size: string;
  tshirt_size: string;
  sweatshirt_size: string;
  fleece_jacket_size: string;
  winter_jacket_size: string;
  work_shoes_size: string;
  helmet_size: string;
  safety_glasses: string;
  gloves_size: string;
  ear_protection: string;
  custom_items: CustomItemPDF[];
}

interface ClothingExportOptions extends PdfOptions {
  signatureUrl?: string;
  signedAt?: string;
  signedByName?: string;
}

// Export employee clothing and safety equipment to PDF
export const exportClothingAndSafetyToPDF = async (
  employeeName: string,
  data: ClothingAndSafetyData,
  options?: ClothingExportOptions
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;
  
  await addCompanyHeader(doc, companyData, logoBase64);
  let yPosition = 45;

  // Title
  yPosition = drawDocumentTitle(doc, "Arbeitskleidung & Sicherheitsausrüstung", `Mitarbeiter: ${employeeName}`, yPosition);
  yPosition += 10;

  // Arbeitskleidung Section
  yPosition = drawSectionHeader(doc, "Arbeitskleidung", yPosition, pageWidth);

  const clothingLabels: Record<string, string> = {
    pants_size: "Hosengröße",
    tshirt_size: "T-Shirt Größe",
    sweatshirt_size: "Sweatshirt Größe",
    fleece_jacket_size: "Fleecejacke Größe",
    winter_jacket_size: "Winterjacke Größe",
    work_shoes_size: "Arbeitsschuhe Größe",
  };

  Object.entries(clothingLabels).forEach(([key, label], index) => {
    if (index % 2 === 0) {
      doc.setFillColor(...CORPORATE_COLORS.tableRowAlt);
      doc.rect(15, yPosition - 5, pageWidth - 30, 12, 'F');
    }
    
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...CORPORATE_COLORS.text);
    doc.text(label + ":", 20, yPosition);
    
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.primary);
    doc.text((data as unknown as Record<string, string>)[key] || "-", pageWidth / 2, yPosition);
    
    yPosition += 12;
  });

  yPosition += 10;

  // Sicherheitsausrüstung Section
  yPosition = drawSectionHeader(doc, "Sicherheitsausrüstung (PSA)", yPosition, pageWidth);

  const safetyLabels: Record<string, string> = {
    helmet_size: "Helm Größe",
    gloves_size: "Handschuhe Größe",
    safety_glasses: "Schutzbrille",
    ear_protection: "Gehörschutz",
  };

  Object.entries(safetyLabels).forEach(([key, label], index) => {
    if (index % 2 === 0) {
      doc.setFillColor(...CORPORATE_COLORS.tableRowAlt);
      doc.rect(15, yPosition - 5, pageWidth - 30, 12, 'F');
    }
    
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...CORPORATE_COLORS.text);
    doc.text(label + ":", 20, yPosition);
    
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.primary);
    doc.text((data as unknown as Record<string, string>)[key] || "-", pageWidth / 2, yPosition);
    
    yPosition += 12;
  });

  // Custom Items Section
  if (data.custom_items && data.custom_items.length > 0) {
    yPosition += 10;
    yPosition = drawSectionHeader(doc, "Weitere Ausrüstung", yPosition, pageWidth);

    data.custom_items.forEach((item, index) => {
      yPosition = checkNewPage(doc, yPosition, 260, logoBase64);
      
      if (index % 2 === 0) {
        doc.setFillColor(...CORPORATE_COLORS.tableRowAlt);
        doc.rect(15, yPosition - 5, pageWidth - 30, 12, 'F');
      }
      
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...CORPORATE_COLORS.text);
      doc.text(item.name + ":", 20, yPosition);
      
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...CORPORATE_COLORS.primary);
      doc.text(item.size || "-", pageWidth / 2, yPosition);
      
      yPosition += 12;
    });
  }

  // Signature section
  if (options?.signatureUrl) {
    yPosition = checkNewPage(doc, yPosition + 15, 200, logoBase64);
    
    yPosition = drawSectionHeader(doc, "Empfangsbestätigung", yPosition, pageWidth);
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.text);
    doc.text("Hiermit bestätige ich den Empfang der oben aufgeführten Arbeitskleidung und Sicherheitsausrüstung.", 20, yPosition);
    yPosition += 10;
    
    try {
      doc.addImage(options.signatureUrl, 'PNG', 20, yPosition, 60, 30);
      yPosition += 35;
    } catch (e) {
      console.warn("Unterschrift konnte nicht geladen werden:", e);
    }
    
    doc.setFontSize(9);
    doc.setTextColor(...CORPORATE_COLORS.textLight);
    if (options.signedByName) {
      doc.text(`Unterzeichnet von: ${options.signedByName}`, 20, yPosition);
      yPosition += 5;
    }
    if (options.signedAt) {
      const signedDate = new Date(options.signedAt);
      doc.text(`Datum: ${signedDate.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit", 
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      })} Uhr`, 20, yPosition);
    }
    doc.setTextColor(0, 0, 0);
  }

  addCompanyFooter(doc, companyData, options?.createdByName);
  doc.save(`Arbeitskleidung_PSA_${employeeName.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`);
};

// Export other costs to PDF
export const exportCostsToPDF = async (
  employeeName: string,
  costs: { date: string; cost_type: string; amount: number; currency: string; payment_frequency: string; description?: string }[],
  monthlyTotal: number,
  options?: PdfOptions
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;
  
  await addCompanyHeader(doc, companyData, logoBase64);
  let yPosition = 45;

  // Title
  yPosition = drawDocumentTitle(doc, "Sonstige Kosten", `Mitarbeiter: ${employeeName}`, yPosition);
  yPosition += 5;

  // Monthly total summary
  yPosition = drawSummaryBox(doc, "Monatliche Gesamtkosten:", monthlyTotal.toLocaleString("de-DE", { style: "currency", currency: "EUR" }), yPosition, pageWidth);
  yPosition += 5;

  const frequencyLabels: Record<string, string> = {
    monthly: "Monatlich",
    quarterly: "Quartalsweise",
    half_yearly: "Halbjährlich",
    yearly: "Jährlich",
    one_time: "Einmalig",
  };

  // Table header
  yPosition = drawTableHeader(doc, [
    { text: "Datum", x: 20 },
    { text: "Kategorie", x: 50 },
    { text: "Betrag", x: 110 },
    { text: "Frequenz", x: 145 },
  ], yPosition, pageWidth);

  costs.forEach((cost, index) => {
    yPosition = checkNewPage(doc, yPosition, 260, logoBase64);

    // Alternating row background
    if (index % 2 === 0) {
      doc.setFillColor(...CORPORATE_COLORS.tableRowAlt);
      doc.rect(15, yPosition - 4, pageWidth - 30, cost.description ? 11 : 7, 'F');
    }

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.text);
    doc.text(new Date(cost.date).toLocaleDateString("de-DE"), 20, yPosition);
    doc.text(cost.cost_type.substring(0, 25), 50, yPosition);
    doc.text(cost.amount.toLocaleString("de-DE", { style: "currency", currency: cost.currency }), 110, yPosition);
    doc.text(frequencyLabels[cost.payment_frequency] || cost.payment_frequency, 145, yPosition);
    
    if (cost.description) {
      yPosition += 4;
      doc.setFontSize(8);
      doc.setTextColor(...CORPORATE_COLORS.textLight);
      doc.text(cost.description.substring(0, 60), 50, yPosition);
    }
    
    yPosition += 7;
  });

  addCompanyFooter(doc, companyData, options?.createdByName);
  doc.save(`Kosten_${employeeName.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`);
};

// Export salary history to PDF
export const exportSalaryToPDF = async (
  employeeName: string,
  salaryHistory: { effective_date: string; base_salary: number; currency: string; notes?: string }[],
  options?: PdfOptions
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;
  
  await addCompanyHeader(doc, companyData, logoBase64);
  let yPosition = 45;

  // Title
  yPosition = drawDocumentTitle(doc, "Gehaltshistorie", `Mitarbeiter: ${employeeName}`, yPosition);
  yPosition += 10;

  // Table header
  yPosition = drawTableHeader(doc, [
    { text: "Gültig ab", x: 20 },
    { text: "Grundgehalt", x: 70 },
    { text: "Begründung", x: 120 },
  ], yPosition, pageWidth);

  salaryHistory.forEach((entry, index) => {
    yPosition = checkNewPage(doc, yPosition, 260, logoBase64);

    // Alternating row background
    if (index % 2 === 0) {
      doc.setFillColor(...CORPORATE_COLORS.tableRowAlt);
      doc.rect(15, yPosition - 4, pageWidth - 30, 8, 'F');
    }

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.text);
    doc.text(new Date(entry.effective_date).toLocaleDateString("de-DE"), 20, yPosition);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...CORPORATE_COLORS.primary);
    doc.text(entry.base_salary.toLocaleString("de-DE", { style: "currency", currency: entry.currency }), 70, yPosition);
    
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.text);
    const notesText = entry.notes ? doc.splitTextToSize(entry.notes, 70) : ["-"];
    doc.text(notesText[0], 120, yPosition);
    
    yPosition += 8;
  });

  addCompanyFooter(doc, companyData, options?.createdByName);
  doc.save(`Gehalt_${employeeName.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`);
};

// Export bonus history to PDF
export const exportBonusToPDF = async (
  employeeName: string,
  bonusHistory: { bonus_date: string; amount: number; currency: string; notes?: string }[],
  options?: PdfOptions
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;
  
  await addCompanyHeader(doc, companyData, logoBase64);
  let yPosition = 45;

  // Title
  yPosition = drawDocumentTitle(doc, "Bonushistorie", `Mitarbeiter: ${employeeName}`, yPosition);
  yPosition += 5;

  // Total summary
  const total = bonusHistory.reduce((sum, b) => sum + b.amount, 0);
  yPosition = drawSummaryBox(doc, "Gesamtsumme Boni:", total.toLocaleString("de-DE", { style: "currency", currency: "EUR" }), yPosition, pageWidth);
  yPosition += 5;

  // Table header
  yPosition = drawTableHeader(doc, [
    { text: "Datum", x: 20 },
    { text: "Betrag", x: 70 },
    { text: "Begründung", x: 120 },
  ], yPosition, pageWidth);

  bonusHistory.forEach((entry, index) => {
    yPosition = checkNewPage(doc, yPosition, 260, logoBase64);

    // Alternating row background
    if (index % 2 === 0) {
      doc.setFillColor(...CORPORATE_COLORS.tableRowAlt);
      doc.rect(15, yPosition - 4, pageWidth - 30, 8, 'F');
    }

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.text);
    doc.text(new Date(entry.bonus_date).toLocaleDateString("de-DE"), 20, yPosition);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...CORPORATE_COLORS.success);
    doc.text(entry.amount.toLocaleString("de-DE", { style: "currency", currency: entry.currency }), 70, yPosition);
    
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.text);
    const notesText = entry.notes ? doc.splitTextToSize(entry.notes, 70) : ["-"];
    doc.text(notesText[0], 120, yPosition);
    
    yPosition += 8;
  });

  addCompanyFooter(doc, companyData, options?.createdByName);
  doc.save(`Bonus_${employeeName.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`);
};

// Export benefits history to PDF
export const exportBenefitsToPDF = async (
  employeeName: string,
  benefitsHistory: { benefit_name: string; start_date: string; end_date?: string | null; monthly_value?: number | null; currency?: string | null; payment_frequency?: string | null; status: string }[],
  monthlyTotal: number,
  options?: PdfOptions
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;
  
  await addCompanyHeader(doc, companyData, logoBase64);
  let yPosition = 45;

  // Title
  yPosition = drawDocumentTitle(doc, "Benefits Übersicht", `Mitarbeiter: ${employeeName}`, yPosition);
  yPosition += 5;

  // Monthly total
  yPosition = drawSummaryBox(doc, "Aktive Benefits monatlich:", monthlyTotal.toLocaleString("de-DE", { style: "currency", currency: "EUR" }), yPosition, pageWidth);
  yPosition += 5;

  // Table header
  yPosition = drawTableHeader(doc, [
    { text: "Benefit", x: 20 },
    { text: "Start", x: 75 },
    { text: "Ende", x: 105 },
    { text: "Betrag", x: 130 },
    { text: "Status", x: 165 },
  ], yPosition, pageWidth);

  benefitsHistory.forEach((entry, index) => {
    yPosition = checkNewPage(doc, yPosition, 260, logoBase64);

    // Alternating row background
    if (index % 2 === 0) {
      doc.setFillColor(...CORPORATE_COLORS.tableRowAlt);
      doc.rect(15, yPosition - 4, pageWidth - 30, 7, 'F');
    }

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.text);
    doc.text(entry.benefit_name.substring(0, 25), 20, yPosition);
    doc.text(new Date(entry.start_date).toLocaleDateString("de-DE"), 75, yPosition);
    doc.text(entry.end_date ? new Date(entry.end_date).toLocaleDateString("de-DE") : "-", 105, yPosition);
    doc.text((entry.monthly_value || 0).toLocaleString("de-DE", { style: "currency", currency: entry.currency || "EUR" }), 130, yPosition);
    
    // Status with color
    if (entry.status === "active") {
      doc.setTextColor(...CORPORATE_COLORS.success);
      doc.text("Aktiv", 165, yPosition);
    } else {
      doc.setTextColor(...CORPORATE_COLORS.textLight);
      doc.text("Inaktiv", 165, yPosition);
    }
    
    yPosition += 7;
  });

  addCompanyFooter(doc, companyData, options?.createdByName);
  doc.save(`Benefits_${employeeName.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`);
};

// Export job description to PDF
interface JobDescriptionContent {
  mainTasks: string;
  responsibilities: string;
  qualifications: string;
  tools: string;
  teamwork: string;
  safety: string;
}

interface JobDescriptionPdfData {
  employeeName: string;
  position: string;
  department?: string;
  content: JobDescriptionContent;
  signatureUrl?: string | null;
  signedAt?: string | null;
  signedByName?: string | null;
}

export const exportJobDescriptionToPDF = async (
  data: JobDescriptionPdfData,
  options?: PdfOptions
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;
  
  await addCompanyHeader(doc, companyData, logoBase64);
  let yPosition = 45;

  // Title
  const positionText = data.department ? `${data.position} (${data.department})` : data.position;
  yPosition = drawDocumentTitle(doc, "Stellenbeschreibung", data.employeeName, yPosition);
  
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...CORPORATE_COLORS.textLight);
  doc.text(positionText, pageWidth / 2, yPosition, { align: "center" });
  yPosition += 15;

  // Separator line
  doc.setDrawColor(...CORPORATE_COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(20, yPosition, pageWidth - 20, yPosition);
  yPosition += 15;

  // Sections
  const sections = [
    { title: "Hauptaufgaben", content: data.content.mainTasks },
    { title: "Verantwortungsbereiche", content: data.content.responsibilities },
    { title: "Qualifikationen & Kenntnisse", content: data.content.qualifications },
    { title: "Werkzeuge & Maschinen", content: data.content.tools },
    { title: "Zusammenarbeit", content: data.content.teamwork },
    { title: "Arbeitssicherheit", content: data.content.safety },
  ];

  for (const section of sections) {
    if (!section.content) continue;

    yPosition = checkNewPage(doc, yPosition, 240, logoBase64);

    // Section header
    yPosition = drawSectionHeader(doc, section.title, yPosition, pageWidth);

    // Section content
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.text);
    
    const lines = doc.splitTextToSize(section.content, pageWidth - 40);
    for (const line of lines) {
      yPosition = checkNewPage(doc, yPosition, 265, logoBase64);
      doc.text(line, 20, yPosition);
      yPosition += 5;
    }
    
    yPosition += 8;
  }

  // Signature section
  if (data.signatureUrl) {
    yPosition = checkNewPage(doc, yPosition, 200, logoBase64);
    yPosition += 10;
    
    // Signature box
    doc.setDrawColor(...CORPORATE_COLORS.border);
    doc.setLineWidth(0.5);
    doc.rect(15, yPosition, pageWidth - 30, 55, 'S');
    
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...CORPORATE_COLORS.primary);
    doc.text("Unterschrift / Bestätigung", 20, yPosition + 8);
    
    // Load and add signature image
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("Failed to load signature"));
        img.src = data.signatureUrl!;
      });
      
      // Add signature image
      doc.addImage(img, 'PNG', 20, yPosition + 12, 60, 25);
    } catch (e) {
      console.warn("Signature image could not be loaded:", e);
      doc.setFontSize(9);
      doc.setTextColor(...CORPORATE_COLORS.textLight);
      doc.text("[Unterschrift konnte nicht geladen werden]", 20, yPosition + 25);
    }
    
    // Signature date and signer info
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.text);
    
    if (data.signedAt) {
      const signedDate = new Date(data.signedAt);
      doc.text(
        `Unterschrieben am: ${signedDate.toLocaleDateString("de-DE")} um ${signedDate.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr`,
        90,
        yPosition + 20
      );
    }
    
    if (data.signedByName) {
      doc.text(`Unterzeichnet von: ${data.signedByName}`, 90, yPosition + 27);
    }
    
    // Legal note
    doc.setFontSize(8);
    doc.setTextColor(...CORPORATE_COLORS.textLight);
    doc.text(
      "Mit der Unterschrift bestätige ich, dass ich die Stellenbeschreibung gelesen und verstanden habe.",
      20,
      yPosition + 48
    );
  }

  addCompanyFooter(doc, companyData, options?.createdByName);
  doc.save(`Stellenbeschreibung_${data.employeeName.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`);
};

/**
 * Work Process PDF Export Types
 */
interface WorkProcessNode {
  id: string;
  type: 'start' | 'end' | 'process' | 'decision';
  label: string;
  description?: string;
  x: number;
  y: number;
}

interface WorkProcessEdge {
  id: string;
  from: string;
  to: string;
  label?: string;
}

interface WorkProcessPdfData {
  title: string;
  description?: string | null;
  departmentName: string;
  flowchart_data: {
    nodes: WorkProcessNode[];
    edges: WorkProcessEdge[];
  };
  createdAt: string;
  renewedAt?: string | null;
}

/**
 * Exports a work process to PDF in DIN A4 format with corporate branding
 */
export const exportWorkProcessToPDF = async (
  data: WorkProcessPdfData,
  options?: PdfOptions
): Promise<void> => {
  const companyData = await getCompanyData();
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);

  // Load logo
  let logoBase64: string | null = null;
  if (companyData.logo_url) {
    logoBase64 = await loadImageAsBase64(companyData.logo_url);
  }

  // Add header
  await addCompanyHeader(doc, companyData, logoBase64);

  let yPosition = 45;

  // Title
  yPosition = drawDocumentTitle(doc, "Arbeitsprozess", data.title, yPosition);
  yPosition += 5;

  // Meta information
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...CORPORATE_COLORS.text);

  const metaY = yPosition;
  drawInfoBox(doc, margin, metaY - 4, contentWidth, 28);

  doc.setFont("helvetica", "bold");
  doc.text("Abteilung:", margin + 5, metaY + 4);
  doc.setFont("helvetica", "normal");
  doc.text(data.departmentName, margin + 40, metaY + 4);

  doc.setFont("helvetica", "bold");
  doc.text("Erstellt am:", margin + 5, metaY + 12);
  doc.setFont("helvetica", "normal");
  doc.text(data.createdAt, margin + 40, metaY + 12);

  if (data.renewedAt) {
    doc.setFont("helvetica", "bold");
    doc.text("Erneuert am:", margin + 5, metaY + 20);
    doc.setFont("helvetica", "normal");
    doc.text(data.renewedAt, margin + 40, metaY + 20);
  }

  yPosition = metaY + 35;

  // Description if provided
  if (data.description) {
    yPosition = drawSectionHeader(doc, "Beschreibung", yPosition, pageWidth);
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.text);
    
    const descLines = doc.splitTextToSize(data.description, contentWidth - 10);
    doc.text(descLines, margin + 5, yPosition + 2);
    yPosition += (descLines.length * 5) + 10;
  }

  // Flowchart content
  yPosition = drawSectionHeader(doc, "Prozessablauf", yPosition, pageWidth);
  yPosition += 5;

  const { nodes, edges } = data.flowchart_data;

  // Helper to get node color based on type
  const getNodeColor = (type: string): [number, number, number] => {
    switch (type) {
      case 'start': return [34, 120, 80];
      case 'end': return [180, 50, 50];
      case 'decision': return [180, 120, 20];
      case 'process':
      default: return CORPORATE_COLORS.primary;
    }
  };

  const getNodeTypeLabel = (type: string): string => {
    switch (type) {
      case 'start': return 'Start';
      case 'end': return 'Ende';
      case 'decision': return 'Entscheidung';
      case 'process':
      default: return 'Schritt';
    }
  };

  // Render each node
  let stepNumber = 1;
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const isProcessNode = node.type === 'process' || node.type === 'decision';
    
    // Calculate needed height for this node
    const labelLines = doc.splitTextToSize(node.label, contentWidth - 25);
    let nodeHeight = 12 + (labelLines.length * 5);
    
    if (node.description && node.description.trim()) {
      const descLines = doc.splitTextToSize(node.description, contentWidth - 30);
      nodeHeight += (descLines.length * 4.5) + 8;
    }

    // Check if we need a new page
    if (yPosition + nodeHeight > pageHeight - 50) {
      doc.addPage();
      if (logoBase64) {
        try { doc.addImage(logoBase64, 'AUTO', 15, 8, 45, 25); } catch (e) {}
      }
      doc.setDrawColor(...CORPORATE_COLORS.primary);
      doc.setLineWidth(0.8);
      doc.line(15, 36, pageWidth - 15, 36);
      yPosition = 45;
      
      // Continue header
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...CORPORATE_COLORS.primary);
      doc.text("Prozessablauf (Fortsetzung)", margin, yPosition);
      yPosition += 10;
    }

    // Draw node type indicator
    const nodeColor = getNodeColor(node.type);
    doc.setFillColor(...nodeColor);
    doc.roundedRect(margin, yPosition - 3, 4, nodeHeight - 2, 1, 1, 'F');

    // Node label with step number for process nodes
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...CORPORATE_COLORS.text);
    
    let labelText = node.label;
    if (node.type === 'process') {
      labelText = `${stepNumber}. ${node.label}`;
      stepNumber++;
    }
    
    const labelLinesRendered = doc.splitTextToSize(labelText, contentWidth - 25);
    doc.text(labelLinesRendered, margin + 8, yPosition + 3);
    
    let contentY = yPosition + 3 + (labelLinesRendered.length * 5);

    // Node type badge
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...nodeColor);
    doc.text(`[${getNodeTypeLabel(node.type)}]`, pageWidth - margin, yPosition + 3, { align: 'right' });

    // Description if present
    if (node.description && node.description.trim()) {
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...CORPORATE_COLORS.textLight);
      
      const descriptionLines = doc.splitTextToSize(node.description, contentWidth - 30);
      doc.text(descriptionLines, margin + 10, contentY + 3);
      contentY += (descriptionLines.length * 4.5) + 5;
    }

    yPosition = contentY + 8;

    // Draw connection arrow if not last node
    if (i < nodes.length - 1) {
      doc.setDrawColor(...CORPORATE_COLORS.border);
      doc.setLineWidth(0.3);
      doc.line(margin + 2, yPosition - 4, margin + 2, yPosition);
      
      // Arrow head
      doc.setFillColor(...CORPORATE_COLORS.border);
      doc.triangle(margin + 2, yPosition + 2, margin, yPosition - 1, margin + 4, yPosition - 1, 'F');
      
      yPosition += 6;
    }
  }

  // Summary box
  yPosition += 5;
  if (yPosition > pageHeight - 60) {
    doc.addPage();
    if (logoBase64) {
      try { doc.addImage(logoBase64, 'AUTO', 15, 8, 45, 25); } catch (e) {}
    }
    doc.setDrawColor(...CORPORATE_COLORS.primary);
    doc.setLineWidth(0.8);
    doc.line(15, 36, pageWidth - 15, 36);
    yPosition = 50;
  }

  yPosition = drawSummaryBox(doc, "Anzahl Schritte:", `${nodes.filter(n => n.type === 'process').length} Prozessschritte`, yPosition, pageWidth);

  // Add footer
  addCompanyFooter(doc, companyData, options?.createdByName);

  // Save PDF
  const fileName = `Arbeitsprozess_${data.title.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`;
  doc.save(fileName);
};

/**
 * Exports all evaluation criteria with descriptions as "Betriebsanweisungen" PDF
 * Each criterion gets its own page with consistent formatting
 */
export const exportCriteriaDescriptionsToPDF = async (
  criteria: Criteria[],
  options?: PdfOptions
) => {
  // NOTE:
  // Browser downloads can be blocked in embedded previews (iframe sandbox) or on iOS Safari.
  // To keep the action user-gesture initiated, we open a fallback tab BEFORE the first await.
  const fallbackWindow = (detectIsInIframe() || detectIsIOS())
    ? window.open("about:blank", "_blank")
    : null;

  if (fallbackWindow) {
    try {
      fallbackWindow.document.title = "PDF wird erstellt…";
      fallbackWindow.document.body.style.fontFamily = "system-ui, -apple-system, Segoe UI, Roboto, sans-serif";
      fallbackWindow.document.body.style.padding = "24px";
      fallbackWindow.document.body.innerHTML =
        "<h2 style=\"margin:0 0 8px 0;\">PDF wird erstellt…</h2><p style=\"margin:0;\">Bitte einen Moment warten. Das Dokument öffnet sich automatisch.</p>";
    } catch {
      // ignore
    }
  }

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;
  
  // Filter active criteria and sort by order
  const activeCriteria = criteria
    .filter(c => c.active)
    .sort((a, b) => a.order - b.order);

  // Helper function to add page header
  const addPageHeader = async (isFirstPage: boolean = false) => {
    if (logoBase64) {
      try { 
        doc.addImage(logoBase64, 'AUTO', 15, 8, 45, 25); 
      } catch (e) {
        console.warn('Failed to add logo to PDF:', e);
      }
    }
    doc.setDrawColor(...CORPORATE_COLORS.primary);
    doc.setLineWidth(0.8);
    doc.line(15, 36, pageWidth - 15, 36);
    return 45;
  };

  // ===== COVER PAGE =====
  await addPageHeader(true);
  let yPosition = 60;

  // Main title
  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...CORPORATE_COLORS.primary);
  doc.text("Betriebsanweisungen", pageWidth / 2, yPosition, { align: "center" });
  yPosition += 12;

  // Subtitle
  doc.setFontSize(14);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...CORPORATE_COLORS.textLight);
  doc.text("Bewertungskriterien und Erläuterungen", pageWidth / 2, yPosition, { align: "center" });
  yPosition += 25;

  // Introduction box
  doc.setFillColor(...CORPORATE_COLORS.backgroundAccent);
  doc.setDrawColor(...CORPORATE_COLORS.border);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin, yPosition, contentWidth, 40, 3, 3, 'FD');
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...CORPORATE_COLORS.text);
  const introText = "Die nachfolgenden Kriterien werden zur Leistungsbewertung der Mitarbeiter herangezogen. Jedes Kriterium ist mit einer Erläuterung versehen, die beschreibt, was bewertet wird und welche Erwartungen erfüllt werden sollen.";
  const splitIntro = doc.splitTextToSize(introText, contentWidth - 10);
  doc.text(splitIntro, margin + 5, yPosition + 10);
  yPosition += 55;

  // Summary on cover page
  const totalPoints = activeCriteria.reduce((sum, c) => sum + c.defaultPoints, 0);
  
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...CORPORATE_COLORS.primary);
  doc.text("Übersicht", margin, yPosition);
  yPosition += 10;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...CORPORATE_COLORS.text);
  doc.text(`• Anzahl Kriterien: ${activeCriteria.length}`, margin + 5, yPosition);
  yPosition += 7;
  doc.text(`• Maximale Gesamtpunktzahl: ${totalPoints} Punkte`, margin + 5, yPosition);
  yPosition += 20;

  // Table of contents
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...CORPORATE_COLORS.primary);
  doc.text("Inhaltsverzeichnis", margin, yPosition);
  yPosition += 10;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...CORPORATE_COLORS.text);

  for (let i = 0; i < activeCriteria.length; i++) {
    const criterion = activeCriteria[i];
    if (yPosition > pageHeight - 40) {
      doc.addPage();
      yPosition = await addPageHeader();
      yPosition += 10;
    }
    
    // TOC entry with dots
    const tocNumber = `${i + 1}.`;
    const tocLabel = criterion.label;
    const tocPage = `Seite ${i + 2}`;
    
    doc.setFont("helvetica", "bold");
    doc.text(tocNumber, margin + 5, yPosition);
    doc.setFont("helvetica", "normal");
    doc.text(tocLabel, margin + 15, yPosition);
    doc.text(tocPage, pageWidth - margin, yPosition, { align: "right" });
    
    yPosition += 7;
  }

  // Add footer to cover page
  addCompanyFooter(doc, companyData, options?.createdByName);

  // ===== CRITERION PAGES =====
  for (let i = 0; i < activeCriteria.length; i++) {
    const criterion = activeCriteria[i];
    
    // New page for each criterion
    doc.addPage();
    yPosition = await addPageHeader();
    yPosition += 10;

    // Criterion number badge
    doc.setFillColor(...CORPORATE_COLORS.primary);
    doc.circle(margin + 10, yPosition + 5, 10, 'F');
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text(`${i + 1}`, margin + 10, yPosition + 9, { align: "center" });

    // Criterion title
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...CORPORATE_COLORS.primary);
    doc.text(criterion.label, margin + 25, yPosition + 8);
    yPosition += 25;

    // Divider line
    doc.setDrawColor(...CORPORATE_COLORS.primary);
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 15;

    // Meta information box
    doc.setFillColor(...CORPORATE_COLORS.backgroundAccent);
    doc.setDrawColor(...CORPORATE_COLORS.border);
    doc.setLineWidth(0.3);
    doc.roundedRect(margin, yPosition, contentWidth, 25, 3, 3, 'FD');

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...CORPORATE_COLORS.textLight);
    doc.text("Maximale Punktzahl:", margin + 5, yPosition + 10);
    doc.text("Gewichtung:", margin + 5, yPosition + 18);
    
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.text);
    doc.text(`${criterion.defaultPoints} Punkte`, margin + 50, yPosition + 10);
    doc.text(`${(criterion.weight * 100).toFixed(0)}%`, margin + 50, yPosition + 18);

    // Type badge
    const typeLabel = criterion.criteriaType === 'bonus' ? 'Bonuskriterium' : 'Standardkriterium';
    const typeBgColor = criterion.criteriaType === 'bonus' ? [255, 243, 205] as [number, number, number] : [220, 237, 200] as [number, number, number];
    const typeTextColor = criterion.criteriaType === 'bonus' ? [133, 100, 4] as [number, number, number] : [56, 118, 29] as [number, number, number];
    
    doc.setFillColor(...typeBgColor);
    doc.roundedRect(pageWidth - margin - 45, yPosition + 7, 40, 12, 2, 2, 'F');
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...typeTextColor);
    doc.text(typeLabel, pageWidth - margin - 25, yPosition + 14, { align: "center" });

    yPosition += 35;

    // Description section
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...CORPORATE_COLORS.primary);
    doc.text("Beschreibung", margin, yPosition);
    yPosition += 10;

    if (criterion.description && criterion.description.trim()) {
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...CORPORATE_COLORS.text);
      
      // Process description text - clean up and format properly
      const cleanDescription = criterion.description
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .trim();
      
      // Split into paragraphs
      const paragraphs = cleanDescription.split(/\n\n+/);
      
      for (const paragraph of paragraphs) {
        if (yPosition > pageHeight - 50) {
          doc.addPage();
          yPosition = await addPageHeader();
          yPosition += 10;
          
          // Continuation header
          doc.setFontSize(10);
          doc.setFont("helvetica", "italic");
          doc.setTextColor(...CORPORATE_COLORS.textLight);
          doc.text(`${criterion.label} (Fortsetzung)`, margin, yPosition);
          yPosition += 10;
          
          doc.setFontSize(11);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(...CORPORATE_COLORS.text);
        }
        
        // Handle bullet points, headings, and regular text
        const lines = paragraph.split('\n');
        for (const line of lines) {
          let trimmedLine = line.trim();
          if (!trimmedLine) continue;
          
          // Clean up Markdown formatting markers AND Unicode formatted text
          const cleanText = (text: string): string => {
            if (!text) return '';
            
            let cleaned = text;
            
            // Remove Markdown bold/italic
            cleaned = cleaned.replace(/\*\*([^*]+)\*\*/g, '$1');
            cleaned = cleaned.replace(/\*([^*]+)\*/g, '$1');
            cleaned = cleaned.replace(/__([^_]+)__/g, '$1');
            cleaned = cleaned.replace(/_([^_]+)_/g, '$1');
            
            // Remove heading markers at start of line
            cleaned = cleaned.replace(/^#{1,6}\s*/, '');
            
            // Remove backticks (code formatting)
            cleaned = cleaned.replace(/`([^`]+)`/g, '$1');
            
            // Remove HTML tags
            cleaned = cleaned.replace(/<[^>]+>/g, '');
            
            // Normalize Unicode mathematical/styled letters to regular ASCII
            // Only process if there are actual unicode characters in that range
            try {
              // Mathematical Bold uppercase (𝐀-𝐙)
              cleaned = cleaned.replace(/[\u{1D400}-\u{1D419}]/gu, (c) => {
                const cp = c.codePointAt(0);
                return cp ? String.fromCharCode(cp - 0x1D400 + 65) : c;
              });
              // Mathematical Bold lowercase (𝐚-𝐳)
              cleaned = cleaned.replace(/[\u{1D41A}-\u{1D433}]/gu, (c) => {
                const cp = c.codePointAt(0);
                return cp ? String.fromCharCode(cp - 0x1D41A + 97) : c;
              });
              // Mathematical Italic uppercase (𝐴-𝑍)
              cleaned = cleaned.replace(/[\u{1D434}-\u{1D44D}]/gu, (c) => {
                const cp = c.codePointAt(0);
                return cp ? String.fromCharCode(cp - 0x1D434 + 65) : c;
              });
              // Mathematical Italic lowercase (𝑎-𝑧)
              cleaned = cleaned.replace(/[\u{1D44E}-\u{1D467}]/gu, (c) => {
                const cp = c.codePointAt(0);
                return cp ? String.fromCharCode(cp - 0x1D44E + 97) : c;
              });
              // Mathematical Bold Italic uppercase (𝑨-𝒁)
              cleaned = cleaned.replace(/[\u{1D468}-\u{1D481}]/gu, (c) => {
                const cp = c.codePointAt(0);
                return cp ? String.fromCharCode(cp - 0x1D468 + 65) : c;
              });
              // Mathematical Bold Italic lowercase (𝒂-𝒛)
              cleaned = cleaned.replace(/[\u{1D482}-\u{1D49B}]/gu, (c) => {
                const cp = c.codePointAt(0);
                return cp ? String.fromCharCode(cp - 0x1D482 + 97) : c;
              });
            } catch (e) {
              // If unicode regex fails, continue with original text
            }
            
            // Small Caps (ᴀ-ᴢ)
            cleaned = cleaned.replace(/[\u1D00-\u1D19]/g, (c) => {
              const code = c.charCodeAt(0);
              return String.fromCharCode(code - 0x1D00 + 97);
            });
            
            // Fullwidth letters
            cleaned = cleaned.replace(/[\uFF21-\uFF3A]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xFF21 + 65));
            cleaned = cleaned.replace(/[\uFF41-\uFF5A]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xFF41 + 97));
            
            // Circled letters
            cleaned = cleaned.replace(/[\u24B6-\u24CF]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0x24B6 + 65));
            cleaned = cleaned.replace(/[\u24D0-\u24E9]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0x24D0 + 97));
            
            // Remove zero-width characters
            cleaned = cleaned.replace(/[\u200B-\u200F\u2028-\u202F\uFEFF]/g, '');
            
            // Normalize multiple spaces to single space
            cleaned = cleaned.replace(/\s+/g, ' ').trim();
            
            return cleaned;
          };
          
          // Check for Markdown heading patterns (# Heading, ## Subheading, ### Sub-subheading)
          const headingMatch = trimmedLine.match(/^(#{1,3})\s+(.+)$/);
          // Check for bullet point patterns
          const bulletMatch = trimmedLine.match(/^[\-•\*]\s+(.+)$/);
          const numberedMatch = trimmedLine.match(/^(\d+[\.\)])\s*(.+)$/);
          
          if (headingMatch) {
            // Markdown heading
            const level = headingMatch[1].length; // 1, 2, or 3
            const headingText = cleanText(headingMatch[2]);
            
            // Font size based on heading level
            const fontSize = level === 1 ? 12 : level === 2 ? 11 : 11;
            
            if (yPosition + 10 > pageHeight - 40) {
              doc.addPage();
              yPosition = await addPageHeader();
              yPosition += 10;
            }
            
            // Add some spacing before headings
            if (level === 1) yPosition += 4;
            
            doc.setFontSize(fontSize);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(...CORPORATE_COLORS.text);
            
            const wrappedLines = doc.splitTextToSize(headingText, contentWidth);
            doc.text(wrappedLines, margin, yPosition);
            yPosition += wrappedLines.length * 6 + 4;
            
            // Reset to normal font
            doc.setFontSize(11);
            doc.setFont("helvetica", "normal");
          } else if (bulletMatch) {
            // Bullet point
            const bulletText = cleanText(bulletMatch[1]);
            const wrappedLines = doc.splitTextToSize(bulletText, contentWidth - 15);
            
            if (yPosition + (wrappedLines.length * 6) > pageHeight - 40) {
              doc.addPage();
              yPosition = await addPageHeader();
              yPosition += 10;
            }
            
            doc.setFontSize(11);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(...CORPORATE_COLORS.text);
            doc.text("•", margin + 3, yPosition);
            doc.text(wrappedLines, margin + 10, yPosition);
            yPosition += wrappedLines.length * 6 + 2;
          } else if (numberedMatch) {
            // Numbered list item
            const number = numberedMatch[1];
            const listText = cleanText(numberedMatch[2]);
            const wrappedLines = doc.splitTextToSize(listText, contentWidth - 18);
            
            if (yPosition + (wrappedLines.length * 6) > pageHeight - 40) {
              doc.addPage();
              yPosition = await addPageHeader();
              yPosition += 10;
            }
            
            doc.setFontSize(11);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(...CORPORATE_COLORS.text);
            doc.text(number, margin + 3, yPosition);
            doc.text(wrappedLines, margin + 12, yPosition);
            yPosition += wrappedLines.length * 6 + 2;
          } else {
            // Regular text - clean and display
            const cleanedLine = cleanText(trimmedLine);
            const wrappedLines = doc.splitTextToSize(cleanedLine, contentWidth);
            
            if (yPosition + (wrappedLines.length * 6) > pageHeight - 40) {
              doc.addPage();
              yPosition = await addPageHeader();
              yPosition += 10;
            }
            
            doc.setFontSize(11);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(...CORPORATE_COLORS.text);
            doc.text(wrappedLines, margin, yPosition);
            yPosition += wrappedLines.length * 6 + 2;
          }
        }
        
        // Paragraph spacing
        yPosition += 4;
      }
    } else {
      doc.setFontSize(10);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(...CORPORATE_COLORS.textLight);
      doc.text("Keine Beschreibung hinterlegt.", margin, yPosition);
      yPosition += 15;
    }

    // Add footer to each criterion page
    addCompanyFooter(doc, companyData, options?.createdByName);
  }

  // Save PDF
  const fileName = `Betriebsanweisungen_Bewertungskriterien_${new Date().toISOString().split("T")[0]}.pdf`;

  const pdfBlob = doc.output("blob") as Blob;
  const pdfUrl = URL.createObjectURL(pdfBlob);

  // Trigger download (best-effort)
  try {
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = fileName;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (e) {
    console.warn("PDF download trigger failed:", e);
  }

  // Fallback: if we opened a tab (iframe/iOS), navigate it to the PDF so the user can save/print.
  if (fallbackWindow && !fallbackWindow.closed) {
    try {
      fallbackWindow.location.href = pdfUrl;
    } catch {
      // ignore
    }
  }

  // Give the browser time to consume the blob URL
  window.setTimeout(() => {
    try {
      URL.revokeObjectURL(pdfUrl);
    } catch {
      // ignore
    }
  }, 60_000);
};
